import apiService from './api.service';

export interface RankingUser {
  id: string;
  name: string;
  avatar: string | null;
  level: number;
  total_xp: number;
  squad_name: string | null;
  sector: string | null;
  position: number;
  xp_change: number;
}

export interface RankingSector {
  id: string;
  name: string;
  total_xp: number;
  member_count: number;
  avg_xp_per_member: number;
  position: number;
  xp_change: number;
}

export interface RankingSquad {
  id: string;
  name: string;
  avatar: string | null;
  category: string;
  total_xp: number;
  member_count: number;
  avg_xp_per_member: number;
  position: number;
  xp_change: number;
}

class RankingsService {
  /**
   * Obtém ranking de pessoas, opcionalmente filtrado por setor
   */
  async getUserRankings(
    limit: number = 100,
    sectorId?: string,
    seasonId?: string
  ): Promise<RankingUser[]> {
    interface ApiRankingUser {
      rank: number;
      user_id: string;
      name: string;
      avatar: string | null;
      level: number;
      xp: number;
      squad_name: string | null;
      sector: string | null;
      trend: 'up' | 'down' | 'stable';
    }
    const response = await apiService.get<ApiRankingUser[]>('/ranking/persons/', {
      params: {
        limit,
        ...(sectorId && { sector_id: sectorId }),
        ...(seasonId && { season_id: seasonId }),
      },
    });
    return response.data.map((item) => ({
      id: item.user_id,
      name: item.name,
      avatar: item.avatar,
      level: item.level,
      total_xp: item.xp,
      squad_name: item.squad_name,
      sector: item.sector,
      position: item.rank,
      xp_change: item.trend === 'up' ? 1 : item.trend === 'down' ? -1 : 0,
    }));
  }

  /**
   * Obtém ranking por setor (usa model Sector, não Squad)
   */
  async getSectorRankings(
    limit: number = 100,
    seasonId?: string
  ): Promise<RankingSector[]> {
    interface ApiSectorRanking {
      rank: number;
      sector_id: string;
      name: string;
      xp: number;
      member_count: number;
      average_xp: number;
      trend: 'up' | 'down' | 'stable';
    }
    const response = await apiService.get<ApiSectorRanking[]>('/ranking/sectors/', {
      params: { limit, ...(seasonId && { season_id: seasonId }) },
    });
    return response.data.map((item) => ({
      id: item.sector_id,
      name: item.name,
      total_xp: item.xp,
      member_count: item.member_count,
      avg_xp_per_member: item.average_xp,
      position: item.rank,
      xp_change: item.trend === 'up' ? 1 : item.trend === 'down' ? -1 : 0,
    }));
  }

  /**
   * Obtém ranking de squads (DEPRECATED — usar getSectorRankings)
   */
  async getSquadRankings(
    _period?: string,
    limit: number = 100,
    seasonId?: string
  ): Promise<RankingSquad[]> {
    interface ApiRankingSquad {
      rank: number;
      squad_id: string;
      name: string;
      badge: string | null;
      xp: number;
      member_count: number;
      average_xp: number;
      trend: 'up' | 'down' | 'stable';
    }
    const response = await apiService.get<ApiRankingSquad[]>('/ranking/squads/', {
      params: { limit, ...(seasonId && { season_id: seasonId }) },
    });
    return response.data.map((item) => ({
      id: item.squad_id,
      name: item.name,
      avatar: item.badge,
      category: '',
      total_xp: item.xp,
      member_count: item.member_count,
      avg_xp_per_member: item.average_xp,
      position: item.rank,
      xp_change: item.trend === 'up' ? 1 : item.trend === 'down' ? -1 : 0,
    }));
  }

  /**
   * Obtém posição do usuário atual
   */
  async getMyPosition(_period?: string, seasonId?: string): Promise<{
    position: number;
    total_users: number;
    percentile: number;
  }> {
    interface ApiMyPosition {
      rank: number;
      user_id: string;
      name: string;
      avatar: string | null;
      level: number;
      xp: number;
      squad_name: string | null;
      sector: string | null;
      trend: string;
    }
    const response = await apiService.get<ApiMyPosition>('/ranking/me/', {
      params: { ...(seasonId && { season_id: seasonId }) },
    });
    return {
      position: response.data.rank,
      total_users: 0,
      percentile: 0,
    };
  }
}

export const rankingsService = new RankingsService();
